import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../Datas/siteConfig";
import "../css/returns.scss";

const returnSteps = [
  {
    title: "Prepare your items",
    text: "Keep products unworn, clean and complete with their original labels and packaging.",
  },
  {
    title: "Register your return",
    text: "Contact customer care with your order number and the items you would like to send back.",
  },
  {
    title: "Pack and send",
    text: "Use sturdy packaging, attach the supplied return label and retain your proof of postage.",
  },
  {
    title: "Receive your refund",
    text: "We inspect the return and email you when the refund has been sent to your original payment method.",
  },
];

function Returns() {
  const { brandName, contact, returnsWindow } = siteConfig;

  return (
    <div className="returns-page">
      <nav className="returns-page__breadcrumb" aria-label="Breadcrumb">
        <Link to="/">Home</Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">Returns</span>
      </nav>

      <header className="returns-page__header">
        <p className="returns-page__eyebrow">Returns and refunds</p>
        <h1>Changed your mind? No problem.</h1>
        <p>
          Online purchases can be returned within {returnsWindow} days of
          delivery. Follow the steps below to keep the process quick and
          straightforward.
        </p>
      </header>

      <main className="returns-page__main">
        <section className="returns-page__notice" aria-label="Return policy summary">
          <strong>{returnsWindow} days</strong>
          <p>
            Start your return within {returnsWindow} days of receiving the
            order. Your statutory rights are not affected.
          </p>
          <Link to="/contact">Start a return</Link>
        </section>

        <section className="returns-page__notice" aria-label="Return address guidance">
          <strong>Return address</strong>
          <p>
            Contact customer care before sending an item. The
            {" "}{siteConfig.countryName} business contact address is not a return
            destination unless our team confirms it in your return instructions.
          </p>
          <Link to="/contact">Request return instructions</Link>
        </section>

        <section className="returns-page__steps" aria-labelledby="return-steps">
          <div className="returns-page__section-heading">
            <p className="returns-page__section-label">Four simple steps</p>
            <h2 id="return-steps">How to return an online order</h2>
          </div>
          <ol>
            {returnSteps.map((step, index) => (
              <li key={step.title}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="returns-page__conditions" aria-labelledby="return-condition-title">
          <div>
            <p className="returns-page__section-label">Before you send it</p>
            <h2 id="return-condition-title">Return condition</h2>
            <p>
              Please try footwear indoors and keep garments away from smoke,
              fragrance and pet hair. We may reduce a refund if handling goes
              beyond what is needed to inspect fit, features and quality.
            </p>
          </div>
          <div className="returns-page__condition-grid">
            <article>
              <h3>Ready to return</h3>
              <ul>
                <li>Unworn and unwashed</li>
                <li>Original labels attached</li>
                <li>Accessories and packaging included</li>
                <li>Order details placed inside the parcel</li>
              </ul>
            </article>
            <article>
              <h3>Contact us first</h3>
              <ul>
                <li>Items received damaged or incomplete</li>
                <li>Possible manufacturing faults</li>
                <li>Incorrect product delivered</li>
                <li>Oversized or unusual equipment</li>
              </ul>
            </article>
          </div>
        </section>

        <section className="returns-page__refunds" aria-labelledby="refund-title">
          <div className="returns-page__section-heading">
            <p className="returns-page__section-label">After it arrives</p>
            <h2 id="refund-title">Refund timing</h2>
          </div>
          <div className="returns-page__table-wrap">
            <table>
              <caption>Typical refund stages</caption>
              <thead>
                <tr>
                  <th scope="col">Stage</th>
                  <th scope="col">Typical timing</th>
                  <th scope="col">What to expect</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="row">Parcel received</th>
                  <td>Carrier dependent</td>
                  <td>Tracking shows delivery to our return location.</td>
                </tr>
                <tr>
                  <th scope="row">Return inspected</th>
                  <td>Up to 10 working days</td>
                  <td>We check the returned items and email an update.</td>
                </tr>
                <tr>
                  <th scope="row">Payment credited</th>
                  <td>Usually 3–5 working days</td>
                  <td>Your bank posts the refund to the original method.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="returns-page__faq" aria-labelledby="returns-faq">
          <div className="returns-page__section-heading">
            <p className="returns-page__section-label">Good to know</p>
            <h2 id="returns-faq">Returns questions</h2>
          </div>
          <div className="returns-page__faq-list">
            <details>
              <summary>Can I exchange an item?</summary>
              <p>
                We process online purchases as returns rather than direct
                exchanges. Place a new order for the preferred size or colour,
                then return the original item.
              </p>
            </details>
            <details>
              <summary>Are original delivery charges refunded?</summary>
              <p>
                When an entire eligible order is returned within the applicable
                cancellation period, the basic outbound delivery charge is
                handled in line with your statutory rights.
              </p>
            </details>
            <details>
              <summary>What if my product may be faulty?</summary>
              <p>
                Do not continue using it. Send photographs, your order number
                and a short description to
                {" "}
                <a href={"mailto:" + contact.email}>{contact.email}</a>. The
                {" "}
                {brandName} team will explain the next step.
              </p>
            </details>
          </div>
        </section>

        <section className="returns-page__support" aria-labelledby="returns-support">
          <div>
            <p className="returns-page__section-label">Still unsure?</p>
            <h2 id="returns-support">Let us look at it with you.</h2>
          </div>
          <div className="returns-page__support-actions">
            <Link className="returns-page__button" to="/contact">
              Contact customer care
            </Link>
            <Link className="returns-page__text-link" to="/shipping">
              View delivery information
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}

export default Returns;
